import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type Facility } from '@reliance/types/models/Facility';
import { type HousingUnit } from '@reliance/types/models/HousingUnit';
import dayjs from 'dayjs';
import { getConfigurationValue } from '../../common/facility';
import config from '../../services/config';
import { getString, setString } from '../../strings';
import { type NodeServerStats } from '../../types/store/actions/NodeServer';
import { type RehydrateAction } from '../../types/store/actions/RehydrateAction';
import { type CommonState } from '../../types/store/states/CommonState';
import { baseDataLoadSuccess } from '../actions/website';
import { getToken } from '../thunks/auth';
import { REHYDRATE } from 'redux-persist';

const initialState: CommonState = {
  facility: null,
  rehydrated: false,
  flaggedInmateDevices: 0,
  devicesWithoutGroups: [],
  stationsCount: 0,
  disabledStationsCount: 0,
  disabledStationsPreview: [],
  housingUnitsWithCommunicationsAltered: [],
};

function loadConfigFromFacility(facility: Facility) {
  config.name = facility.name;
  config.features = [];
  // If we get a new timezone value, set dayjs default value
  if (config.timezone != facility.timezone) {
    dayjs.tz.setDefault(facility.timezone);
  }
  config.timezone = facility.timezone;

  let features: string[] = [];

  if (Array.isArray(facility.features)) {
    features = facility.features;
  } else {
    features = JSON.parse(facility.features);
  }

  features.map((feature) => {
    config.features = config.features ?? [];
    switch (feature) {
      case 'Video Calls':
        config.features.push('video-call');
        break;
      case 'Reference Material':
        config.features.push('reference-material');
        break;
      case 'Job Search':
        config.features.push('job-search');
        break;
      case 'Inmate Requests':
        config.features.push('inmate-requests');
        break;
      case 'Commissary Ordering':
        config.features.push('commissary');
        break;
      case 'Law Library':
        config.features.push('law-library');
        break;
      case 'Kiosk Audio Calls':
        config.features.push('audio-call');
        break;
      case 'Wireless Audio Calls':
        config.features.push('wireless-audio-calls');
        break;
      case 'Wireless Video Calls':
        config.features.push('video-call');
        break;
      case 'Wireless Video Visitations':
        config.features.push('visitation');
        break;
      case 'Prepaid Collect Calls':
        config.features.push('audio-call');
        break;
      case 'Voicemail':
        config.features.push('voicemail');
        break;
      case 'Video Visitation':
        config.search.visitation.visitTypes = {
          ...config.search.visitation.visitTypes,
          visitation: 'Video Visitation',
        };
        config.features.push('visitation');
        break;
      case 'Audio Visitation':
        config.search.visitation.visitTypes = {
          ...config.search.visitation.visitTypes,
          'audio-visitation': 'Audio Visitation',
        };
        config.features.push('audio-visitation');
        break;
      case 'Inmate Communication Policy':
        config.features.push('inmate-communication-policy');
        break;
      case 'Transcription':
        config.features.push('transcription');
        break;
      case 'Music':
        config.features.push('music');
        break;
      case 'Reference Materials':
        config.features.push('reference-material');
        break;
      case 'Person Search':
        config.features.push('person-search');
        break;
      case 'Mail Connect':
        config.features.push('mail-connect');
        break;
      case 'Games':
        config.features.push('games');
        break;
    }
  });

  if (facility.featureTexting) {
    config.features.push('texting');
  }

  if (getConfigurationValue('VisitTimeLimitMinutes', facility)) {
    config.visitation.defaultTimeLimit = Number(
      getConfigurationValue('VisitTimeLimitMinutes', facility) ?? '',
    );
  }

  if (getConfigurationValue('VisitTimeLimitOptions', facility)) {
    config.visitation.timeLimitOptions = JSON.parse(
      getConfigurationValue('VisitTimeLimitOptions', facility) ?? '',
    );
  }

  if (getConfigurationValue('VisitationVisitorOptionalFields', facility)) {
    config.visitation.visitationVisitorOptionalFields = (
      getConfigurationValue('VisitationVisitorOptionalFields', facility) ?? ''
    ).split(',');
  }

  if (getConfigurationValue('InmatePINLength', facility)) {
    config.inmate.pinLength = Number(
      getConfigurationValue('InmatePINLength', facility),
    );
  }

  if (getConfigurationValue('RelcoreSiteBrand', facility)) {
    setString(
      'Brand',
      getConfigurationValue('RelcoreSiteBrand', facility) ?? '',
    );
    setString(
      'ResultsApiError',
      getString('ResultsApiError').replace(
        'Reliance Telephone',
        getConfigurationValue('RelcoreSiteBrand', facility) ?? '',
      ),
    );
  }

  config.inmate.housingUnitsEnabled = facility.housingUnits?.length > 0;
  config.nodeServer =
    import.meta.env.VITE_NODE_SERVER ||
    'https://services.reliancetelephone.com';

  // If we get this value, assume that it is a site based site
  if (getConfigurationValue('ConnectServerIp', facility)) {
    config.connectServer!.url =
      import.meta.env.VITE_CONNECT_SERVER_URL ??
      (location.protocol === 'https:' ? 'wss://' : 'ws://') +
        window.location.hostname +
        '/connect-server';
  }
}

const common = createSlice({
  reducerPath: undefined,
  selectors: undefined,
  reducers: {},
  name: 'auth',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(
        baseDataLoadSuccess,
        (state, action: PayloadAction<Facility>) => {
          loadConfigFromFacility(action.payload);
          state.facility = action.payload;
          state.housingUnitsWithCommunicationsAltered =
            action.payload.housingUnits?.filter((unit: HousingUnit) => {
              if (
                unit.audioCallingEnabled != null &&
                unit.videoCallingEnabled != null &&
                unit.textingEnabled != null &&
                unit.communicationsEnabled != null
              ) {
                return (
                  !unit.audioCallingEnabled ||
                  !unit.videoCallingEnabled ||
                  !unit.textingEnabled ||
                  !unit.communicationsEnabled
                );
              }
              return false;
            });
        },
      )
      .addCase(getToken.fulfilled, (state, action) => {
        const facilityUser = action.payload.data.facilityUser;
        if (!facilityUser) {
          return;
        }

        const facility = facilityUser.facility;

        // Set config values
        loadConfigFromFacility(facility);
        state.facility = facility;
        state.housingUnitsWithCommunicationsAltered =
          facility.housingUnits.filter((unit: HousingUnit) => {
            if (unit.audioCallingEnabled != null) {
              return !unit.audioCallingEnabled;
            }
            return false;
          });
      })
      .addCase('NODE_SERVER/STATS', (state, action: NodeServerStats) => {
        const statsFacility = action.data.facility;

        state.flaggedInmateDevices = action.data.flaggedInmateDevices;
        state.devicesWithoutGroups = action.data.devicesWithoutGroups;
        state.facility = statsFacility;
      })
      .addCase(REHYDRATE, (state, action: RehydrateAction) => {
        const rehydrated = false;
        if (action.key == 'root') {
          state.rehydrated = true;
        }
        // Set facility if available
        if (
          action &&
          action.payload &&
          action.payload.common &&
          action.payload.common.facility
        ) {
          const facility = action.payload.common.facility;

          // Set config values
          loadConfigFromFacility(facility);
          state.rehydrated = rehydrated;
          state.facility = facility;
        }
      });
  },
});

export default common.reducer;
